import React, { useState, useEffect, useCallback, useRef, FunctionComponent, ChangeEvent } from 'react';
import { Presentation } from './presentation';
import type { Props } from './types';

const sanitizeIndustryIds = (ids: string[]): number[] => {
  return ids
    .filter((id) => id)
    .map(Number)
    .filter((id) => !isNaN(id));
};

export const Component: FunctionComponent<Props> = (props) => {
  const [companyName, setCompanyName] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('q') || '';
  });

  const [selectedIndustries, setSelectedIndustries] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tmpIndustryIds = searchParams.get('industryIds');
    return tmpIndustryIds ? tmpIndustryIds.split(',') : props.industryId ? [props.industryId.toString()] : [];
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tmpCompanyName, setTmpCompanyName] = useState('');
  const [tmpSelectedIndustries, setTmpSelectedIndustries] = useState<string[]>([]);
  const [isSearched, setIsSearched] = useState(Boolean(companyName || selectedIndustries.length > 0));

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleInputClick = useCallback(() => {
    setIsModalOpen(true);
    setTmpCompanyName(companyName);
    setTmpSelectedIndustries([...selectedIndustries]);
    $('body, html').css('overflow', 'hidden');
  }, [companyName, selectedIndustries]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTmpCompanyName('');
    setTmpSelectedIndustries([]);
    $('body, html').css('overflow', 'visible');
  }, []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (isModalOpen) {
        setTmpCompanyName(value);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          props.updateFilteredCount(value, sanitizeIndustryIds(tmpSelectedIndustries));
        }, 500);
      } else {
        setCompanyName(value);
      }
    },
    [isModalOpen, props.updateFilteredCount, tmpSelectedIndustries]
  );

  const handleIndustryChange = useCallback(
    (industryId: number) => {
      const industryIdStr = industryId.toString();
      const updateIndustries = (industries: string[]) =>
        industries.includes(industryIdStr)
          ? industries.filter((id) => id !== industryIdStr)
          : [...industries, industryIdStr];
      if (isModalOpen) {
        setTmpSelectedIndustries((prevState) => {
          const newTempIndustries = updateIndustries(prevState);
          props.updateFilteredCount(tmpCompanyName, sanitizeIndustryIds(newTempIndustries));
          return newTempIndustries;
        });
      } else {
        setSelectedIndustries((prevState) => updateIndustries(prevState));
        if (props.onIndustryChange) {
          props.onIndustryChange(industryId);
        }
      }
    },
    [isModalOpen, props.updateFilteredCount, tmpCompanyName, props.onIndustryChange]
  );

  const handleClear = useCallback(() => {
    setCompanyName('');
    setSelectedIndustries([]);
    setTmpCompanyName('');
    setTmpSelectedIndustries([]);
    setIsSearched(false);
    props.onSearch('', [], true);
  }, [props.onSearch]);

  const handleSearch = useCallback(() => {
    $('body, html').css('overflow', 'visible');

    setCompanyName(tmpCompanyName);
    setSelectedIndustries(tmpSelectedIndustries);
    setIsSearched(true);
    setIsModalOpen(false);

    props.onSearch(tmpCompanyName, sanitizeIndustryIds(tmpSelectedIndustries), true);
  }, [tmpCompanyName, tmpSelectedIndustries, props.onSearch]);

  useEffect(() => {
    setCompanyName(props.q);
    setSelectedIndustries(props.industryIds);
    setIsSearched(Boolean(props.q || props.industryIds.length > 0));
  }, [props.q, props.industryIds]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const presentationProps = {
    ...props,
    companyName,
    tmpCompanyName,
    isModalOpen,
    tmpSelectedIndustries,
    handleInputClick,
    handleCloseModal,
    handleInputChange,
    handleIndustryChange,
    handleClear,
    handleSearch,
  };

  return <Presentation {...presentationProps} />;
};
